import { useEffect, useState } from "react";
import { useReactMediaRecorder } from "react-media-recorder";

export interface UseAudioRecordingResult {
  isRecording: boolean;
  recordingTime: number;
  startRecording: () => void;
  stopRecording: () => void;
  recordingBlob: Blob | null;
  status: string;
}

// Detect the best audio MIME type for the current browser
function getBestAudioMimeType(): string {
  const types = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
    "audio/wav",
  ];

  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }

  return "";
}

export function useAudioRecording(): UseAudioRecordingResult {
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);

  // Configure audio constraints for better mobile compatibility
  const mimeType = getBestAudioMimeType();
  const { stopRecording, startRecording, mediaBlobUrl, status } =
    useReactMediaRecorder({
      audio: true,
      ...(mimeType && { mediaRecorderOptions: { mimeType } }),
      askPermissionOnMount: false,
    });

  const isRecording = status === "recording";

  // Count recording time
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    if (isRecording) {
      setRecordingTime(0);
      intervalId = setInterval(() => {
        setRecordingTime(prevTime => prevTime + 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isRecording]);

  // Convert mediaBlobUrl to Blob
  useEffect(() => {
    const convertToBlob = async () => {
      if (mediaBlobUrl) {
        try {
          const response = await fetch(mediaBlobUrl);
          const blob = await response.blob();

          // Log blob info for debugging
          console.log("Recording blob created:", {
            size: blob.size,
            type: blob.type,
            url: mediaBlobUrl,
          });

          // Ensure the blob has some data
          if (blob.size === 0) {
            console.error("Recording blob is empty!");
            return;
          }

          setRecordingBlob(blob);
        } catch (error) {
          console.error("Error converting mediaBlobUrl to blob:", error);
        }
      }
    };

    convertToBlob();
  }, [mediaBlobUrl]);

  return {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,
    recordingBlob,
    status,
  };
}
